// API Configuration
export const API_CONFIG = {
  BASE_URL: import.meta.env.SORARE_ACE_API_BASE_URL,
  VERSION: 'v1',
  TIMEOUT: 30000, // 30 seconds
};

// API Version path
export const API_VERSION = `/${API_CONFIG.VERSION}`;

// HTTP Methods
export const HTTP_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  DELETE: 'DELETE',
  PATCH: 'PATCH',
};

// Query Parameters
export const QUERY_PARAMS = {
  RARITY: 'rarity',
  OFFSET: 'offset',
  LIMIT: 'limit',
  SORT: 'sort',
  ORDER: 'order',
  SEARCH: 'search',
  SEARCH_TEXT: 'searchText',
  FILTER: 'filter',
  POSITION: 'position',
  SCORES_TYPE: 'scoresType',
  SCORES_SORT: 'scoresSort',
  CARDS_SORT: 'cardsSort',
  NBA_SCORES_SORT: 'nbaScoresSort',
  NBA_LINEUP_SORT: 'nbaLineupSort',
  RARITY_PRICES: 'rarityPrices',
};

// Default values
export const DEFAULT_VALUES = {
  OFFSET: 0,
  TOP_PLAYERS_LIMIT: 100,
  NEXT_GAMES_LIMIT: 10,
  // Note: LIMIT is dynamically calculated based on screen dimensions
  // See useCardsData hook for the calculation logic
};

// Default pagination for the player search endpoint
export const SEARCH_DEFAULTS = {
  OFFSET: 0,
  LIMIT: 10,
};

// Screen-related constants for dynamic limit calculation
export const SCREEN_CONSTANTS = {
  CARD_WIDTH: 232, // Width of each card in pixels
  CARDS_PER_ROW_MULTIPLIER: 2, // Multiplier for cards per row calculation
};

// Player-related endpoints
export const PLAYER_ENDPOINTS = {
  // Get player data
  GET_PLAYER: (slug: string) => `players/${slug}`,

  // Get NBA player data
  GET_NBA_PLAYER: (slug: string) => `players/nba/${slug}`,

  // Get player listed cards (params pre-built via buildQueryParams)
  GET_PLAYER_LISTED: (slug: string, params: string) => `players/${slug}/listed${params}`,

  // Get player auctions (params pre-built via buildQueryParams)
  GET_PLAYER_AUCTIONS: (slug: string, params: string) => `players/${slug}/auctions${params}`,

  // Get player rewards (params pre-built via buildQueryParams)
  GET_PLAYER_REWARDS: (slug: string, params: string) => `players/${slug}/rewards${params}`,

  // Get player instant buys (params pre-built via buildQueryParams)
  GET_PLAYER_INSTANT: (slug: string, params: string) => `players/${slug}/instant${params}`,

  // Get filtered player scores (params pre-built via buildQueryParams)
  GET_PLAYER_SCORES: (slug: string, params: string) => `players/${slug}/scores${params}`,

  // Get player's upcoming games (params pre-built via buildQueryParams)
  GET_PLAYER_NEXT_GAMES: (slug: string, params: string) => `players/${slug}/next-games${params}`,

  // Update player scores
  UPDATE_PLAYER_SCORES: (slug: string) => `players/${slug}/scores/update`,

  // Reset player scores
  RESET_PLAYER_SCORES: (slug: string) => `players/${slug}/scores/reset`,

  // Get top players
  GET_TOP_PLAYERS: (params: string) => `players/top${params}`,

  // Get top NBA players
  GET_TOP_NBA_PLAYERS: (params: string) => `players/nba/top${params}`,

  // Search players (params pre-built via buildQueryParams)
  SEARCH_PLAYERS: (params: string) => `players/search${params}`,
};

// Manager-related endpoints
export const MANAGER_ENDPOINTS = {
  // Get manager cards
  GET_MANAGER_CARDS: (slug: string, params: string) => `managers/${slug}/cards${params}`,

  // Get NBA lineup
  GET_NBA_LINEUP: (slug: string, params: string) => `managers/${slug}/nba/lineup${params}`,

  // Refresh manager card
  REFRESH_MANAGER_CARD: (slug: string, cardSlug: string) =>
    `managers/${slug}/cards/${cardSlug}/update`,

  // Refresh all manager cards
  REFRESH_ALL_MANAGER_CARDS: (slug: string) => `managers/${slug}/cards/refresh`,
};

// Market-related endpoints
export const MARKET_ENDPOINTS = {
  // Get all cards
  GET_ALL_CARDS: (params: string) => `cards/all${params}`,

  // Get listed cards
  GET_LISTED_CARDS: (params: string) => `cards/listed${params}`,

  // Get instant buy cards
  GET_INSTANT_CARDS: (params: string) => `cards/instant${params}`,

  // Get auctions
  GET_AUCTIONS: (params: string) => `cards/auctions${params}`,

  // Get rewards
  GET_REWARDS: (params: string) => `cards/rewards${params}`,
};

// Typed card-source identifiers used to route infinite card queries to the
// correct market/manager fetcher (see queries/useCards.ts). Replaces the old
// apiPath substring matching.
export const CardSource = {
  ALL: 'all',
  LISTED: 'listed',
  INSTANT: 'instant',
  AUCTIONS: 'auctions',
  MANAGER: 'manager',
} as const;

export type CardSourceValue = (typeof CardSource)[keyof typeof CardSource];

// Dashboard-related endpoints
export const DASHBOARD_ENDPOINTS = {
  // Get stats summary
  GET_STATS_SUMMARY: () => 'stats/summary',
};

// Auth / Sorare-connection endpoints
export const AUTH_ENDPOINTS = {
  // Current connection state; always 200, `manager` is null when not connected
  GET_SESSION: () => 'auth/session',

  // Drop the session cookie
  LOGOUT: () => 'auth/logout',
};

/**
 * Absolute URL that starts the Sorare OAuth flow.
 *
 * Unlike every other entry here this is NOT an axios path: connecting is a full-page
 * browser navigation to the API (which 302s to Sorare and back), so it needs the origin
 * and version prefix that `apiClient`'s `baseURL` would otherwise supply.
 *
 * `returnTo` is where the API sends the browser afterwards. It is validated server-side
 * (relative paths only) — never trust it round-tripping unchanged.
 */
export const buildSorareConnectUrl = (returnTo?: string) => {
  const base = `${API_CONFIG.BASE_URL}/${API_CONFIG.VERSION}/auth/sorare/start`;
  return returnTo ? `${base}?returnTo=${encodeURIComponent(returnTo)}` : base;
};

// Complete API endpoints object for easy access
export const API_ENDPOINTS = {
  PLAYER: PLAYER_ENDPOINTS,
  MANAGER: MANAGER_ENDPOINTS,
  MARKET: MARKET_ENDPOINTS,
  DASHBOARD: DASHBOARD_ENDPOINTS,
  AUTH: AUTH_ENDPOINTS,
};

// Common query parameter builders
export const QUERY_BUILDERS = {
  // Build pagination parameters with dynamic limit
  pagination: (offset = DEFAULT_VALUES.OFFSET, limit?: number) => {
    const params = new URLSearchParams();
    params.append(QUERY_PARAMS.OFFSET, offset.toString());
    if (limit !== undefined) {
      params.append(QUERY_PARAMS.LIMIT, limit.toString());
    }
    return `?${params.toString()}`;
  },

  // Build rarity filter
  rarity: (rarity: string) => `?${QUERY_PARAMS.RARITY}=${rarity}`,

  // Build search parameter
  search: (searchTerm: string) => `?${QUERY_PARAMS.SEARCH}=${encodeURIComponent(searchTerm)}`,

  // Build sort parameters
  sort: (sortBy: string, order = 'desc') =>
    `?${QUERY_PARAMS.SORT}=${sortBy}&${QUERY_PARAMS.ORDER}=${order}`,
};

// Error messages
export const API_ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error occurred',
  TIMEOUT_ERROR: 'Request timeout',
  SERVER_ERROR: 'Server error occurred',
  NOT_FOUND: 'Resource not found',
  UNAUTHORIZED: 'Unauthorized access',
  FORBIDDEN: 'Access forbidden',
  VALIDATION_ERROR: 'Validation error',
  PARSE_ERROR: 'Invalid server response',
};

// HTTP Status codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  TIMEOUT: 408,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
};
